// web/src/registry/features/_part-4.ts —— FEATURES 的第 4/6 段（20 个元素：ticketing / coach / operations / customer-insights / analytics）
// 拆自 web/src/registry/features.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { Feature } from '../features';

export const FEATURES_PART_4: Feature[] = [

  {
    id: 'ticketing.analytics',
    title: '工单分析',
    domain: 'ticketing',
    scope: 'domain',
    type: 'analytics',
    route: '/tickets/analytics',
    orphan: true,
    permission: null,
    priority: 'P2',
    issue: '已收敛到「运营分析 → 报表中心」，不再占业务域导航',
  },

  // 智能演练
  {
    id: 'coach.practice',
    title: '开始演练',
    domain: 'coach',
    scope: 'domain',
    type: 'workspace',
    route: '/coach',
    legacyLocations: ['/coach'],
    permission: 'coach.practice',
    nav: { section: 'business', group: 'coach', order: 1 },
    priority: 'P2',
  },
  {
    id: 'coach.history',
    title: '演练记录',
    domain: 'coach',
    scope: 'domain',
    type: 'record',
    route: '/coach/history',
    legacyLocations: ['/coach?tab=history'],
    permission: 'coach.history',
    nav: { section: 'business', group: 'coach', order: 2 },
    priority: 'P2',
  },
  {
    id: 'coach.bank',
    title: '题库',
    domain: 'coach',
    scope: 'domain',
    type: 'resource',
    route: '/coach/bank',
    legacyLocations: ['/coach?tab=bank'],
    permission: 'coach.bank',
    nav: { section: 'business', group: 'coach', order: 3 },
    priority: 'P2',
  },

  // 运营监控
  {
    id: 'operations.agents',
    title: '坐席状态',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/agents',
    legacyLocations: ['/ops-center?tab=agents'],
    permission: 'operations.agents',
    nav: { section: 'business', group: 'operations', order: 1 },
    priority: 'P1',
    issue: '原为 /ops-center 页内 Tab',
  },
  {
    id: 'operations.queues',
    title: '队列实况',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/queues',
    legacyLocations: ['/ops-center?tab=queues'],
    permission: 'operations.queues',
    nav: { section: 'business', group: 'operations', order: 2 },
    priority: 'P1',
  },
  {
    id: 'operations.cockpit',
    title: '运营驾驶舱',
    domain: 'operations',
    scope: 'domain',
    type: 'analytics',
    route: '/operations/cockpit',
    legacyLocations: ['/ops-center?tab=cockpit', '/cockpit'],
    permission: 'operations.cockpit',
    nav: { section: 'business', group: 'operations', order: 3 },
    priority: 'P1',
  },
  {
    id: 'operations.screen',
    title: '监控大屏',
    domain: 'operations',
    scope: 'domain',
    type: 'analytics',
    route: '/operations/screen',
    legacyLocations: ['/monitor/screen'],
    permission: 'operations.screen',
    nav: { section: 'business', group: 'operations', order: 4 },
    priority: 'P1',
  },
  {
    id: 'operations.shifts',
    title: '排班',
    domain: 'operations',
    scope: 'domain',
    type: 'resource',
    route: '/operations/shifts',
    legacyLocations: ['/shift'],
    permission: 'operations.shifts',
    nav: { section: 'business', group: 'operations', order: 5 },
    priority: 'P1',
    issue: '原挂在“全渠道服务”下，属现场运营',
  },
  {
    // 重大事件台账：值班长 / 运营把影响面大的事立成一条事件并跟踪到闭环。
    // 权限沿用「监控大屏」页面码（同一受众：admin+supervisor），不新增后端权限码。
    id: 'operations.incidents',
    title: '重大事件',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/incidents',
    permission: 'operations.screen',
    nav: { section: 'business', group: 'operations', order: 6 },
    priority: 'P1',
  },
  {
    // 班组作战台：已整块并入「仪表盘」（组长视图），不再占侧栏入口；路由与深链保留，便于值班长/管理员直达。
    // 权限沿用「坐席状态」页面码——受众与数据面完全相同，不再新增页面码。
    id: 'operations.team',
    title: '班组作战台',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/team',
    permission: 'operations.agents',
    priority: 'P2',
  },
  {
    // 当班指挥台：已整块并入「仪表盘」（值班长视图），不再占侧栏入口；路由与深链保留。
    // 权限沿用「监控大屏」页面码（同一受众：admin+supervisor）。
    id: 'operations.duty',
    title: '当班指挥台',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/duty',
    permission: 'operations.screen',
    priority: 'P2',
  },
  {
    // 人员分配：把 B 组的人临时调到排队的 A 组；溢出与兜底在队列实况页长期配置，重大事件有独立菜单页。
    id: 'operations.duty.control',
    title: '人员分配',
    domain: 'operations',
    scope: 'domain',
    type: 'workspace',
    route: '/operations/duty/control',
    permission: 'operations.screen',
    priority: 'P2',
  },
  {
    id: 'operations.settings.alerts',
    title: '驾驶舱预警设置',
    domain: 'operations',
    scope: 'domain',
    type: 'config',
    route: '/operations/settings/alerts',
    legacyLocations: ['/ops-center/settings'],
    permission: 'operations.settings.alerts',
    priority: 'P2',
  },
  {
    id: 'operations.settings.agents',
    title: '坐席运营设置',
    domain: 'operations',
    scope: 'domain',
    type: 'config',
    route: '/operations/settings/agents',
    legacyLocations: ['/system/agent-base-settings'],
    permission: 'operations.settings.agents',
    priority: 'P0',
    issue: '小休原因/默认上线属现场运营规则，原埋在 /system；身份属性才进 identity',
  },
  {
    id: 'operations.settings',
    title: '运营监控设置',
    domain: 'operations',
    scope: 'domain',
    type: 'config',
    route: '/operations/settings',
    // 设置 Landing：任一子配置权限即可见（当前仅 admin 持有），页内 Tab 按各自子码裁剪
    permission: ['operations.settings', 'operations.settings.alerts', 'operations.settings.agents'],
    nav: { section: 'business', group: 'operations', order: 9 },
    priority: 'P1',
    issue: 'Settings Landing：聚合驾驶舱预警/坐席运营设置',
  },
  {
    id: 'operations.analytics',
    title: '指标分析',
    domain: 'operations',
    scope: 'domain',
    type: 'analytics',
    route: '/operations/analytics',
    orphan: true,
    legacyLocations: ['/report'],
    permission: null,
    priority: 'P1',
    issue: '已收敛到「运营分析 → 报表中心」，不再占业务域导航',
  },
  // 客户洞察（满意度 + VOC 闭环：修改/分析的是 Customer Feedback / Experience，与 operations 的 Agent/Queue/Shift 不同类）
  {
    id: 'customer-insights.satisfaction',
    title: '满意度评价',
    domain: 'customer-insights',
    scope: 'domain',
    type: 'analytics',
    route: '/customer-insights/satisfaction',
    legacyLocations: ['/satisfaction'],
    permission: 'customer-insights.satisfaction',
    nav: { section: 'business', group: 'customer-insights', order: 1 },
    priority: 'P1',
    issue: '电话+在线双渠道评价，业务对象为 Customer Feedback（V2 未覆盖，Phase 1.5 补域）',
  },
  {
    id: 'customer-insights.settings.satisfaction',
    title: '满意度设置',
    domain: 'customer-insights',
    scope: 'domain',
    type: 'config',
    route: '/customer-insights/settings/satisfaction',
    orphan: true,
    legacyLocations: ['/satisfaction/config'],
    permission: 'customer-insights.settings.satisfaction',
    priority: 'P1',
    issue: '原菜单/路由/代码三方不一致（菜单全渠道、路由 /system）',
  },
  {
    id: 'customer-insights.voc',
    title: '客户之声 VOC',
    domain: 'customer-insights',
    scope: 'domain',
    type: 'analytics',
    route: '/customer-insights/voc',
    legacyLocations: ['/voc'],
    permission: 'customer-insights.voc',
    nav: { section: 'business', group: 'customer-insights', order: 2 },
    priority: 'P1',
    issue: '业务对象为 Customer Experience（V2 未覆盖，Phase 1.5 补域）',
  },
  {
    id: 'customer-insights.settings',
    title: '客户洞察设置',
    domain: 'customer-insights',
    scope: 'domain',
    type: 'config',
    route: '/customer-insights/settings',
    // 设置 Landing：任一子配置权限即可见（当前仅 admin 持有），页内 Tab 按各自子码裁剪
    permission: ['customer-insights.settings', 'customer-insights.settings.satisfaction', 'customer-insights.settings.voc-labels'],
    nav: { section: 'business', group: 'customer-insights', order: 3 },
    priority: 'P1',
    issue: '页内 Tab 聚合满意度设置/VOC 标签管理（原 Settings Landing 卡片）',
  },
  {
    id: 'customer-insights.settings.voc-labels',
    title: 'VOC 标签管理',
    domain: 'customer-insights',
    scope: 'domain',
    type: 'config',
    route: '/customer-insights/settings/voc-labels',
    orphan: true,
    legacyLocations: ['/system/voc-labels'],
    permission: 'customer-insights.settings.voc-labels',
    priority: 'P1',
    issue: '原挂在 /system 下',
  },

  // 报表中心（业务分区下，含三个分组子菜单）
  {
    id: 'analytics.report.core',
    title: '核心报表',
    domain: 'analytics',
    scope: 'domain',
    type: 'analytics',
    route: '/report/core',
    permission: 'analytics.report',
    nav: { section: 'business', group: 'analytics', order: 1 },
    priority: 'P1',
    issue: '报表中心的菜单码一度缺失（permission=null → 全角色可见），而读端无权限点，坐席可读全量运营报表',
  },
  {
    id: 'analytics.report.ai',
    title: 'AI 分析',
    domain: 'analytics',
    scope: 'domain',
    type: 'analytics',
    route: '/report/ai',
    permission: 'analytics.report',
    nav: { section: 'business', group: 'analytics', order: 2 },
    priority: 'P1',
  },];
